import { Hidden } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import Banner from '@cpg-its/ads.containers.banner';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { LabelSettingProps } from '../../global/models/epi-model';
import { TcsmEpiApiState, TcsmStates } from '../../root/tcsm-model';
import { GuidedProcessSupportProps, SearchProps } from '../arc/models/arc-epi-props';
import { PageContainer } from '../../global/components/layout/page-container';
import { MainContent } from '../../global/components/layout/main-content-container';
import { PageHeader } from '../../global/components/layout/page-header';
import { SideBarContent } from '../../global/components/layout/sidebar-content';
import { GuidedProcessSupportBlock } from '../../global/components/guided-process-support-block';
import { SearchApp } from '../../search/components/search-app';
import { sortTableStyles } from '../../global/constants/theme/styles/tables/generic-sort-table';
import { SearchList } from '../../search/components/search-list';
import { ListPagination } from '../../search/components/list-pagination';
import { SortButton } from '../../search/components/sort';
import { getFormattedDate } from '../../global/helpers/date-helpers';
import { getTcsmEpiProps, setSearchSettings } from '../../global/ducks/epi-api';
import { getMyDocuments } from '../../global/ducks/person-prc-api';
import { setMyDocuments } from './my-documents-slice';
import { MyDocumentsItem } from './my-documents-models';
import { ContentSpacer } from '../../global/components/layout/generic-content-spacer';
import { downloadFileFromApi } from '../../global/helpers/file-helpers';
import { buttonStyles } from '../../global/constants/theme/styles/components/buttonstyles';
import { stringSort } from '../../global/helpers/sort-helper';

interface Props {
	searchSettingsId: string;	
}

function MyDocumentsList(props: Props) {
	const [loading, setLoading] = useState(false);
    const [hasError, setHasError] = useState(false);    
	
	const dispatch = useDispatch();
	const token = useSelector<TcsmStates, string>(state => state.authenticatedState.accessToken);
	const language = useSelector<TcsmStates, string>(state => state.globalization.currentlanguage);
	const epiApiState = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
	const labelSettings = useSelector<TcsmStates, LabelSettingProps>(a => a.epiProps?.labelSettingState?.epiContent);
	const searchSettings = useSelector<TcsmStates, SearchProps>(state => state.epiProps.searchSettingsState.epiContent);
	const gpsSettings = useSelector<TcsmStates, GuidedProcessSupportProps>(state => state.epiProps?.gpsSettingsState?.epiContent);
	const guid = useSelector<TcsmStates, string>(state => state.authenticatedState?.clientGuid);	
	const documentsLoaded = useSelector<TcsmStates, boolean>(state => state.myDocumentsState.contentLoaded);
	const initialList = useSelector<TcsmStates, MyDocumentsItem[]>(state => state.myDocumentsState.documents);	

	const tableClasses = sortTableStyles();
	const buttonClasses = buttonStyles();        

	useEffect(() => {		
		if (!epiApiState?.searchSettingsState?.contentLoaded && props) {
			dispatch(getTcsmEpiProps(dispatch, props?.searchSettingsId, { guid }, language, false, setSearchSettings));
		}
	}, [props]);

	useEffect(() => {			
		if (!documentsLoaded){
			dispatch(getMyDocuments(guid,				
				(response) => { 
					// success
					const remoteDocuments = (response.data ? response.data : response) as MyDocumentsItem[];
					const sortedDocuments = remoteDocuments?.sort((a, b) => stringSort(b.createdAt, a.createdAt));
					dispatch(setMyDocuments(sortedDocuments));															
				}, () => { 
					// failure
					dispatch(setMyDocuments(null));
				}));					
		}	
	}, [guid]);

	if (loading || 
		!documentsLoaded || 
		!epiApiState?.searchSettingsState?.contentLoaded ||
		!epiApiState?.labelSettingState?.contentLoaded ) {
		return <LoadingCircle />;
	}

	const downloadFile = (url, fileName) => {
        setHasError(false);
        setLoading(true);
        downloadFileFromApi(
            url, 
            token, 
            fileName, 
            ()=>{
                setLoading(false);
                setHasError(false);
            }, 
            ()=>{
                setHasError(true);
                setLoading(false);
            });
    };

    function RenderError(){
        if (!hasError) return null;
        return (
        <ContentSpacer>
            <Banner
                banner={{
                    type: 'error',
                    label: <p className='input-label'> {labelSettings?.myDocumentsDownloadError} </p>,
                }}
            />
        </ContentSpacer>);
    }

	function MyDocumentsListLabels(){
		return (
			<div className='sort-table-row'>
				<div className='sort-table-row-cell'>
					<SortButton label={labelSettings?.dateUploadedLabel} name='createdAt' />
				</div>
				<div className='sort-table-row-cell'>
					<SortButton label={labelSettings?.documentLinkLabel} name='name' />
				</div>
			</div>
		);
	}

	function MyDocumentIcon(item: MyDocumentsItem){
		const isPDF =  item?.contentURL?.includes('.pdf') || item?.name?.includes('.pdf');

		if (isPDF){
			return (<PictureAsPdfIcon />);
		}

		return null;
	}

	function MyDocumentsItemView(listItem: any){
		const casted = listItem?.item as MyDocumentsItem;
		
		if (!casted) return null;

		const documentFullName = casted.document_type_name || casted.name; 
		let documentFilename = casted.name;
		const extensionIndex = casted.name?.lastIndexOf('.');

		if (casted.document_type_name && extensionIndex > 0){
			documentFilename = `${casted.document_type_name}${casted.name.substring(extensionIndex)}`;
		}
		
		return (
		<div className='sort-table-row'>
            <div className='sort-table-row-cell'>
                <Hidden smUp>
                    <p className='input-label'>{labelSettings?.dateUploadedLabel}</p>
                </Hidden>
                <p>
					{getFormattedDate(casted.createdAt)}                    
                </p>
            </div>
            <div className='sort-table-row-cell'>
                <Hidden smUp>
                    <p className='input-label'>{labelSettings?.documentLinkLabel}</p>
                </Hidden>
                <p>
					<button className={buttonClasses.linkButton} type='button' onClick={() => downloadFile(`${casted.contentURL}`, documentFilename)}>
						{ MyDocumentIcon(casted) }
						<span>{documentFullName}</span>
					</button>				   
                </p>
            </div>
        </div>
		);
	}

	function RenderContent() {	
		
		if (initialList?.length > 0){
			return (			
				<SearchApp initialList={initialList} appName='MyDocumentsList'>				
				<div className={tableClasses.sortTable}>
					<Hidden xsDown>
						<MyDocumentsListLabels />
					</Hidden>
					<div className='sort-table-content'>
						<SearchList
							renderList={
								(item) => {
									return <MyDocumentsItemView key={item?.doc_id} item={item} />;
								}
							}
							renderPagination={
								(totalCounts, name) => {
									return <ListPagination name={name} totalCounts={totalCounts} totalPages={Math.ceil(totalCounts / 15)} nextButton={searchSettings?.nextButton} prevButton={searchSettings?.previousButton} />;
								}
							}
							emptyMessage={labelSettings?.myDocumentsEmptyListMessage}
							/>
					</div>
				</div>
				</SearchApp>			
			);
		}

		return (
			<p>{labelSettings?.myDocumentsEmptyListMessage}</p>
		);
	}

	return (
		<PageContainer>
			<MainContent>
				<PageHeader headerText={labelSettings?.myDocumentsTitle} description={labelSettings?.myDocumentsDescription} />
				<Hidden mdUp>
					<SideBarContent>
						<GuidedProcessSupportBlock title={gpsSettings?.myDocumentsGpsTitle} contents={gpsSettings?.myDocumentsGps} />
					</SideBarContent>
				</Hidden>
				<RenderError/>
				<RenderContent />
			</MainContent>

			<Hidden smDown>
				<SideBarContent>
					<GuidedProcessSupportBlock title={gpsSettings?.myDocumentsGpsTitle} contents={gpsSettings?.myDocumentsGps} />
				</SideBarContent>
			</Hidden>
		</PageContainer>
	);
}

export default MyDocumentsList;